import { useCallback, useEffect, useState } from 'react';

import { AssessmentDocumentViewModel } from '../types';
import {
  DomainDetailsViewModel,
  ProgressFlowViewModel,
} from '../utils/assessment-mapper';
import { needsToSave as checkNeedsToSave } from '../utils/assessment-workflow-validators';

interface UseDomainEditorProps {
  domainDetailsViewModel: DomainDetailsViewModel | undefined | null;
  progressFlowViewModel: ProgressFlowViewModel | undefined;
  files: AssessmentDocumentViewModel[];
  deletedFiles: Set<string>;
}

/**
 * Per-domain editor state: the user-mutable toggles (`isApplicable`,
 * `noEvidenceChecked`) and the save-time validation surface
 * (`validationError`).
 *
 * The state is reset on every domain change (synced from the API) so the page
 * doesn't have to remember to re-initialize when the user navigates to a new
 * domain. `validationError` self-clears as soon as the user takes corrective
 * action (adds/removes a file, toggles applicability, etc.).
 *
 * `needsToSave()` is exposed here because the source-of-truth for "are there
 * unsaved changes?" lives next to the editor state it inspects.
 */
export const useDomainEditor = ({
  domainDetailsViewModel,
  progressFlowViewModel,
  files,
  deletedFiles,
}: UseDomainEditorProps) => {
  const [noEvidenceChecked, setNoEvidenceChecked] = useState<boolean>(false);
  const [isApplicable, setIsApplicable] = useState<boolean>(true);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Re-initialize from API every time the active domain changes.
  useEffect(() => {
    setIsApplicable(domainDetailsViewModel?.isApplicable ?? true);
    setNoEvidenceChecked(domainDetailsViewModel?.isSkipped ?? false);
    setValidationError(null);
  }, [
    domainDetailsViewModel
  ]);

  // Clear validation error when the user takes corrective action.
  useEffect(() => {
    if (validationError) {
      setValidationError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files.length, noEvidenceChecked, isApplicable]);

  const needsToSave = useCallback((): boolean => {
    if (!domainDetailsViewModel?.id || !progressFlowViewModel) return false;
    return checkNeedsToSave(Array.from(files.values())) || deletedFiles.size > 0;
  }, [domainDetailsViewModel?.id, progressFlowViewModel, files, deletedFiles]);

  return {
    noEvidenceChecked,
    setNoEvidenceChecked,
    isApplicable,
    setIsApplicable,
    validationError,
    setValidationError,
    needsToSave,
  };
};
